import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { QueryKeys, MutationKeys, dataService } from 'librechat-data-provider';
import type { UseQueryResult, UseMutationResult } from '@tanstack/react-query';
import type {
  TCompanyPolicy,
  TCompanyAnalytics,
  TCompanyUsersRequest,
  TCompanyInviteRequest,
  TCompanyUsersResponse,
  TCompanyGroupsResponse,
  TCompanyMutationResponse,
  TCompanyAnalyticsRequest,
  TUpdateCompanyPolicyRequest,
} from 'librechat-data-provider';

/** Companies this user may manage — their own for a Lead HR, all of them for an admin. */
export const useCompanyGroupsQuery = (enabled: boolean): UseQueryResult<TCompanyGroupsResponse> =>
  useQuery<TCompanyGroupsResponse>([QueryKeys.companyGroups], () => dataService.getCompanyGroups(), {
    enabled,
    refetchOnWindowFocus: false,
  });

export const useCompanyPolicyQuery = (groupId: string | undefined): UseQueryResult<TCompanyPolicy> =>
  useQuery<TCompanyPolicy>(
    [QueryKeys.companyPolicy, groupId],
    () => dataService.getCompanyPolicy(groupId as string),
    { enabled: !!groupId, refetchOnWindowFocus: false },
  );

export const useCompanyUsersQuery = (
  groupId: string | undefined,
  params: TCompanyUsersRequest,
): UseQueryResult<TCompanyUsersResponse> =>
  useQuery<TCompanyUsersResponse>(
    [QueryKeys.companyUsers, groupId, params],
    () => dataService.getCompanyUsers(groupId as string, params),
    { enabled: !!groupId, keepPreviousData: true, refetchOnWindowFocus: false },
  );

export const useCompanyAnalyticsQuery = (
  groupId: string | undefined,
  params: TCompanyAnalyticsRequest,
): UseQueryResult<TCompanyAnalytics> =>
  useQuery<TCompanyAnalytics>(
    [QueryKeys.companyAnalytics, groupId, params],
    () => dataService.getCompanyAnalytics(groupId as string, params),
    { enabled: !!groupId, keepPreviousData: true, refetchOnWindowFocus: false },
  );

/**
 * Invite, revoke and remove all move seat usage and the membership table, so each
 * refreshes both. Seat counts come back on the response, but refetching keeps the view
 * honest when another Lead HR is working the same company.
 */
function useMembershipMutation<TVars>(
  mutationKey: string,
  mutationFn: (vars: TVars) => Promise<TCompanyMutationResponse>,
): UseMutationResult<TCompanyMutationResponse, Error, TVars> {
  const queryClient = useQueryClient();
  return useMutation(mutationFn, {
    mutationKey: [mutationKey],
    onSuccess: () => {
      queryClient.invalidateQueries([QueryKeys.companyUsers]);
      queryClient.invalidateQueries([QueryKeys.companyAnalytics]);
    },
  });
}

export const useInviteCompanyUserMutation = (): UseMutationResult<
  TCompanyMutationResponse,
  Error,
  { groupId: string; payload: TCompanyInviteRequest }
> =>
  useMembershipMutation<{ groupId: string; payload: TCompanyInviteRequest }>(
    MutationKeys.inviteCompanyUser,
    ({ groupId, payload }) => dataService.inviteCompanyUser(groupId, payload),
  );

export const useRevokeCompanyInviteMutation = (): UseMutationResult<
  TCompanyMutationResponse,
  Error,
  { groupId: string; email: string }
> =>
  useMembershipMutation<{ groupId: string; email: string }>(
    MutationKeys.revokeCompanyInvite,
    ({ groupId, email }) => dataService.revokeCompanyInvite(groupId, email),
  );

/** Removes someone from the company only — their account and history survive. */
export const useRemoveCompanyUserMutation = (): UseMutationResult<
  TCompanyMutationResponse,
  Error,
  { groupId: string; userId: string }
> =>
  useMembershipMutation<{ groupId: string; userId: string }>(
    MutationKeys.removeCompanyUser,
    ({ groupId, userId }) => dataService.removeCompanyUser(groupId, userId),
  );

/** Saves the company's policy text and the vocabularies its people choose from. */
export const useUpdateCompanyPolicyMutation = (): UseMutationResult<
  TCompanyPolicy,
  Error,
  { groupId: string; payload: TUpdateCompanyPolicyRequest }
> => {
  const queryClient = useQueryClient();
  return useMutation(
    ({ groupId, payload }) => dataService.updateCompanyPolicy(groupId, payload),
    {
      mutationKey: [MutationKeys.updateCompanyPolicy],
      onSuccess: (data, variables) => {
        queryClient.setQueryData([QueryKeys.companyPolicy, variables.groupId], data);
        /** The table's region and level filters are built from these vocabularies. */
        queryClient.invalidateQueries([QueryKeys.companyUsers]);
      },
    },
  );
};
